/**
 * PlatformerJuice — the "game feel" layer, kept out of the scene so
 * PlatformerScene stays an orchestrator. One instance per stage mount.
 *
 * Owns four small systems, all driven from the scene's fixed step:
 *
 *   1. SFX — procedural chip-style WebAudio blips (jump, land, coin,
 *      stomp, damage, power-up, bump). No asset files needed; always
 *      available; per-stage audio can layer on top later via the
 *      existing Music Note / sound-zone pipeline.
 *   2. Dust — one shared ParticleSystem for landing puffs, run dust
 *      and stomp bursts (preset-configured, burst-only).
 *   3. Squash & stretch — short scale tweens on the player sprite:
 *      stretch tall on jump launch, squash flat on landing.
 *   4. Screen shake + hit-stop — forwards trauma to the camera and
 *      asks the scene to freeze a few frames on big hits.
 *
 * Scene contract:
 *   const juice = new PlatformerJuice({ camera, worldContainer,
 *       requestHitStop: (frames) => …, theme });
 *   juice.update(player)      — once per fixed step, AFTER player.update
 *   juice.onVerdict(kind, e)  — pickup/stomp/damage/powerup verdicts
 *   juice.destroy()
 */

import { ParticleSystem, emitterConfigFromProps } from './ParticleSystem.js';

// ── procedural SFX ────────────────────────────────────────────────
//
// Each effect is a tiny oscillator envelope on a shared AudioContext —
// the classic NES/Game-Boy vocabulary: rising square = jump, noise
// thud = land, two-tone ping = coin, descending saw = damage, arpeggio
// = power-up. Volumes sit well under music level.

const SFX_DEFS = {
    jump:    { type: 'square',   from: 320, to: 660,  dur: 0.16, vol: 0.16 },
    land:    { type: 'triangle', from: 180, to: 70,   dur: 0.10, vol: 0.18 },
    coin:    { type: 'square',   from: 980, to: 1320, dur: 0.09, vol: 0.14, step: true },
    stomp:   { type: 'square',   from: 240, to: 90,   dur: 0.14, vol: 0.20 },
    damage:  { type: 'sawtooth', from: 300, to: 60,   dur: 0.34, vol: 0.20 },
    powerup: { type: 'square',   from: 392, to: 784,  dur: 0.40, vol: 0.16, arp: [392, 494, 587, 784] },
    bump:    { type: 'triangle', from: 140, to: 90,   dur: 0.08, vol: 0.16 },
};

class ChipSfx {
    constructor() {
        this._ctx = null;
        this._muted = false;
    }

    _ensureCtx() {
        if (this._ctx) return this._ctx;
        try {
            const AC = window.AudioContext || window.webkitAudioContext;
            this._ctx = AC ? new AC() : null;
        } catch (_) { this._ctx = null; }
        return this._ctx;
    }

    play(name) {
        if (this._muted) return;
        const def = SFX_DEFS[name];
        const ctx = def && this._ensureCtx();
        if (!ctx) return;
        // Autoplay policy: contexts start suspended until a gesture.
        // Gameplay always follows input, so resume() succeeds here.
        if (ctx.state === 'suspended') ctx.resume?.().catch?.(() => {});
        try {
            const t0 = ctx.currentTime;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = def.type;
            if (def.arp) {
                // Stepped arpeggio (power-up fanfare).
                const stepDur = def.dur / def.arp.length;
                def.arp.forEach((f, i) => osc.frequency.setValueAtTime(f, t0 + i * stepDur));
            } else if (def.step) {
                // Two-tone step (coin ping).
                osc.frequency.setValueAtTime(def.from, t0);
                osc.frequency.setValueAtTime(def.to, t0 + def.dur * 0.45);
            } else {
                osc.frequency.setValueAtTime(def.from, t0);
                osc.frequency.exponentialRampToValueAtTime(Math.max(20, def.to), t0 + def.dur);
            }
            gain.gain.setValueAtTime(def.vol, t0);
            gain.gain.exponentialRampToValueAtTime(0.001, t0 + def.dur);
            osc.connect(gain).connect(ctx.destination);
            osc.start(t0);
            osc.stop(t0 + def.dur + 0.02);
        } catch (_) { /* best-effort */ }
    }

    destroy() {
        try { this._ctx?.close?.(); } catch (_) {}
        this._ctx = null;
    }
}

// ── squash & stretch ──────────────────────────────────────────────

/** Frames a squash/stretch pulse takes to relax back to 1:1. */
const SS_RELAX_FRAMES = 12;

// ── the juice controller ──────────────────────────────────────────

export class PlatformerJuice {
    /**
     * @param {{ camera: import('./PlatformerCamera.js').PlatformerCamera,
     *           worldContainer: import('../../../vendor/pixi.min.mjs').Container,
     *           requestHitStop: (frames: number) => void }} opts
     */
    constructor({ camera, worldContainer, requestHitStop }) {
        this._camera = camera;
        this._requestHitStop = requestHitStop || (() => {});
        this._sfx = new ChipSfx();

        // One shared burst-only dust system, parented to the world so
        // puffs scroll with the level. Warm grey, soft shapes, slight
        // upward drift, fast fade — reads as kicked-up dirt.
        this._dust = new ParticleSystem(worldContainer, emitterConfigFromProps({
            rate: 0,                      // burst-only
            lifetime: 0.45,
            speedMin: 30, speedMax: 90,
            angle: 0, spread: 70,         // up-ish cone
            gravity: -30,                 // drift up like disturbed dust
            damping: 3,
            startColor: '#d6cdc2', endColor: '#d6cdc2',
            startSize: 9, endSize: 2, sizeRandom: 0.5,
            startAlpha: 0.55, endAlpha: 0,
            shape: 'soft', blend: 'normal',
            maxParticles: 200,
        }));
        this._dust.setEmitting(false);

        // Squash & stretch state on the player display.
        this._ssScaleX = 1;
        this._ssScaleY = 1;
        this._ssFrames = 0;
        this._runDustCooldown = 0;
    }

    // ── per-step driver ─────────────────────────────────

    /**
     * Advance the juice systems one fixed 60 Hz step. Call AFTER the
     * player has updated so the just-jumped / just-landed flags are
     * fresh for this frame.
     */
    update(player) {
        // Event flags from PlayerEntity.update.
        if (player.justJumped) {
            player.justJumped = false;
            this._sfx.play('jump');
            this._pulse(0.74, 1.26);                       // stretch tall
        }
        if (player.justLanded) {
            const impact = Math.min(1, player.justLanded / 16);  // vs terminal velocity
            player.justLanded = 0;
            this._sfx.play('land');
            this._pulse(1 + 0.34 * impact, 1 - 0.30 * impact);   // squash flat
            this._burstAtFeet(player, 6 + Math.round(10 * impact));
            if (impact > 0.55) this._camera?.addTrauma(0.18 * impact);
        }
        if (player._lastBump) this._sfx.play('bump');

        // Run dust: kicked up while sprinting on the ground.
        if (this._runDustCooldown > 0) this._runDustCooldown -= 1;
        if (player.onGround && Math.abs(player.vx) > 5 && this._runDustCooldown <= 0) {
            this._runDustCooldown = 7;
            this._dust.setOrigin(
                player.x + player.w / 2 - Math.sign(player.vx) * player.w * 0.4,
                player.y + player.h - 2,
            );
            this._dust.burst(2);
        }

        // Relax squash & stretch back to 1:1 and write it onto the
        // player sprite (display container scale, anchored via position
        // compensation so the feet stay planted).
        this._applySquashStretch(player);

        // Step the dust particles at the fixed 60 Hz rate.
        this._dust.update(1 / 60);
    }

    /** Scene collision verdicts → sound + shake + hit-stop. */
    onVerdict(kind, entity = null) {
        switch (kind) {
            case 'pickup':
                this._sfx.play('coin');
                break;
            case 'stomp':
                this._sfx.play('stomp');
                this._requestHitStop(3);
                this._camera?.addTrauma(0.22);
                if (entity) {
                    this._dust.setOrigin(entity.x + entity.w / 2, entity.y + entity.h / 2);
                    this._dust.burst(14);
                }
                break;
            case 'damage':
                this._sfx.play('damage');
                this._requestHitStop(4);
                this._camera?.addTrauma(0.45);
                break;
            case 'powerup':
                this._sfx.play('powerup');
                break;
            default:
                break;
        }
    }

    // ── internals ───────────────────────────────────────

    /** Start a squash/stretch pulse (x, y scale multipliers). */
    _pulse(sx, sy) {
        this._ssScaleX = sx;
        this._ssScaleY = sy;
        this._ssFrames = SS_RELAX_FRAMES;
    }

    _applySquashStretch(player) {
        const d = player.display;
        if (!d) return;
        if (this._ssFrames > 0) {
            this._ssFrames -= 1;
            const t = this._ssFrames / SS_RELAX_FRAMES;     // 1 → 0
            const ease = t * t;                              // ease-out
            const sx = 1 + (this._ssScaleX - 1) * ease;
            const sy = 1 + (this._ssScaleY - 1) * ease;
            d.scale.set(sx, sy);
            // Keep the feet planted + the body centred while scaled:
            // display.x/y normally equal player.x/y (top-left).
            d.x = player.x + (player.w * (1 - sx)) / 2;
            d.y = player.y + player.h * (1 - sy);
        } else if (d.scale.x !== 1 || d.scale.y !== 1) {
            d.scale.set(1, 1);
            d.x = player.x;
            d.y = player.y;
        }
    }

    _burstAtFeet(player, n) {
        this._dust.setOrigin(player.x + player.w / 2, player.y + player.h - 2);
        this._dust.burst(n);
    }

    destroy() {
        try { this._dust?.destroy(); } catch (_) {}
        this._dust = null;
        this._sfx.destroy();
        this._camera = null;
    }
}
